import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useContext, useState } from "react";
import { MaterialTableColumns } from "../../../../../configs/provider";
import { Material, Provider } from "../../../../../data/entities";
import { ROLE } from "../../../../../data/enums";
import { AuthenticationContext } from "../../../../../provider/AuthenticationProvider";
import CustomTable from "../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../../shared/components/IconButton";
import SearchBarWithSuggestion from "../../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import TextBox from "../../../../../shared/components/TextBox";
import CustomButton from "../../../../../shared/components/TitleRow/CustomButton";
import { api } from "../../../../../shared/hooks/api";
import { PageContentWrapper } from "../../../../../shared/styled/PageContentStyle";
import { ContentWrapper, StyledNavLink } from "../../../../../shared/styled/TableStyles";
import DeleteIcon from "../../../../../shared/svg/DeleteIcon";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../../utils/format";
import { error } from "../../../../../utils/toast";
import AddMaterialModal from "./AddMaterialModal";
import { MaterialButtonRow, MaterialCounter, MaterialTableTitle } from "./Styled";

interface MaterialRowProps {
    item: any;
    extraRowProps: {
        removeItem: (material: Material) => void;
        onChangeMaterialPrice: (material: Material, price: number) => void;
        canEdit: boolean;
        isStorageKeeper: boolean;
        relationKey?: "materials" | "outsource_materials";
        hideRemove?: boolean;
    };
}

const MaterialRow = ({ item, extraRowProps }: MaterialRowProps) => {
    const price = item.providerPrice ? item.providerPrice : 0;
    const { isStorageKeeper } = extraRowProps;
    const isOutsourceMaterialList = extraRowProps.relationKey === "outsource_materials";
    const [isExpanded, setIsExpanded] = useState(false);
    const outsourceTableColumnCount = isStorageKeeper ? 5 : 6;
    const outsourceStandards = Array.isArray(item.outsource_standards)
        ? item.outsource_standards
        : [];

    const getStandardValue = (standard: any, field: string) =>
        standard?.[field] ?? standard?.source_material?.[field];

    const renderOutsourceStandards = () => (
        <TableRow>
            <TableCell
                colSpan={outsourceTableColumnCount}
                sx={{
                    padding: "8px 16px 12px 42px",
                    backgroundColor: "#f5f5f5",
                }}
            >
                <div
                    style={{
                        width: "80%",
                        maxWidth: "100%",
                        margin: "0 auto 8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: "#555",
                    }}
                >
                    Định mức cho 1 đơn vị gia công
                    <StyledNavLink
                        to={`/materials/${item.id}/?tab=outsource-standard`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Xem định mức vật tư
                    </StyledNavLink>
                </div>
                <Table
                    size="small"
                    sx={{
                        width: "80%",
                        maxWidth: "100%",
                        margin: "0 auto",
                        backgroundColor: "#fff",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        overflow: "hidden",
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700, padding: "4px 8px" }}>
                                Mã vật tư
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700, padding: "4px 8px" }}>
                                Vật tư nguồn
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700, padding: "4px 8px" }}>
                                Số lượng / 1 đơn vị gia công
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700, padding: "4px 8px" }}>
                                Đơn vị
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {outsourceStandards.length > 0 ? (
                            outsourceStandards.map((standard: any) => {
                                const standardId = getStandardValue(standard, "id");
                                const materialCode = getStandardValue(standard, "material_code") || standardId;
                                const materialName = getStandardValue(standard, "name") || "-";
                                const quantity = standard.source_quantity_per_output ?? "-";
                                const unit = getStandardValue(standard, "measurement_unit") || "-";

                                return (
                                    <TableRow key={standardId}>
                                        <TableCell sx={{ fontSize: 11, padding: "4px 8px" }}>
                                            <StyledNavLink
                                                to={`/materials/${standardId}/`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {materialCode}
                                            </StyledNavLink>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 11, padding: "4px 8px" }}>
                                            {materialName}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 11, padding: "4px 8px" }}>
                                            {quantity}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 11, padding: "4px 8px" }}>
                                            {unit}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} sx={{ fontSize: 11, padding: "6px 8px" }}>
                                    Chưa có định mức vật tư nguồn
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableCell>
        </TableRow>
    );

    return (
        <>
            <TableRow
                sx={isOutsourceMaterialList && isExpanded ? { backgroundColor: "#e1f2fc" } : undefined}
            >
                <StyledTableCell align="left">
                    {isOutsourceMaterialList && (
                        <button
                            type="button"
                            aria-label={isExpanded ? "Thu gọn định mức" : "Mở rộng định mức"}
                            onClick={() => setIsExpanded((expanded) => !expanded)}
                            style={{
                                border: 0,
                                background: "transparent",
                                cursor: "pointer",
                                padding: "0 12px 0 0",
                            }}
                        >
                            {isExpanded ? "▾" : "▸"}
                        </button>
                    )}
                    {isOutsourceMaterialList ? (
                        <StyledNavLink
                            to={`/materials/${item.id}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {item.material_code || item.id}
                        </StyledNavLink>
                    ) : (
                        <StyledNavLink to={`/materials/${item.id}/`}>
                            {item.name}
                        </StyledNavLink>
                    )}
                </StyledTableCell>
                {isOutsourceMaterialList && (
                    <StyledTableCell align="left">{item.name}</StyledTableCell>
                )}
                <StyledTableCell align="left">
                    {item.measurement_unit}
                </StyledTableCell>
                {!isStorageKeeper && (
                    <StyledTableCell align="left">
                        <TextBox
                            numeric
                            text={formatInternationalNumber(price ?? "")}
                            onChangeText={(value: string) => {
                                const n = parseInternationalToNumber(value, error);
                                if (!Number.isFinite(n)) return;
                                extraRowProps.onChangeMaterialPrice(item, n);
                            }}
                            disabled={!extraRowProps.canEdit}
                        />
                    </StyledTableCell>
                )}
                <StyledTableCell align="left"> {item.note} </StyledTableCell>
                <StyledTableCell align="left">
                    {!extraRowProps.hideRemove && (
                        <IconButton
                            onClick={() => extraRowProps.removeItem(item)}
                            disabled={!extraRowProps.canEdit}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </StyledTableCell>
            </TableRow>
            {isOutsourceMaterialList && isExpanded && renderOutsourceStandards()}
        </>
    );
};

interface MaterialListProps {
    providerData: Provider;
    mergeProvider: Function;
    relationKey?: "materials" | "outsource_materials";
    title?: string;
    counterLabel?: string;
    priceColumnLabel?: string;
    addButtonLabel?: string;
    emptyText?: string;
    searchPlaceholder?: string;
}

const MaterialList = ({
    providerData,
    mergeProvider,
    relationKey = "materials",
    title = "Danh sách vật tư",
    counterLabel = "vật tư",
    priceColumnLabel = "Giá cung cấp",
    addButtonLabel = "Thêm Vật Tư",
    emptyText = "Không có vật tư nào được chọn",
    searchPlaceholder = "Tìm kiếm vật tư...",
}: MaterialListProps) => {
    const materials = (providerData as any)[relationKey] || [];
    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canEdit = [ROLE.MANAGER, ROLE.LOGISTIC_STAFF, ROLE.LOGISTIC_MANAGER].includes(role);
    const isStorageKeeper = [ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    const [{ data: allMaterialsData }, fetchAllMaterials] = api.get(
        "/materials/",
        { page_size: Number.MAX_SAFE_INTEGER, option: relationKey },
    );
    const allMaterials = allMaterialsData?.results ?? [];
    const totalMaterials = allMaterialsData?.count ?? 0;

    const handleSearchSelectMaterial = (item: Material, isAdded: boolean) => {
        const alreadyIn = materials.some((m: any) => m.id === item.id);
        if (isAdded && !alreadyIn) {
            mergeProvider({ [relationKey]: [...materials, { ...item, providerPrice: 0 }] });
        } else if (!isAdded && alreadyIn) {
            mergeProvider({ [relationKey]: materials.filter((m: any) => m.id !== item.id) });
        }
    };

    const tableItems = materials;

    const materialColumns = isStorageKeeper
        ? MaterialTableColumns.filter(column => column.text !== "Giá cung cấp")
        : MaterialTableColumns.map((column) =>
            column.text === "Giá cung cấp" ? { ...column, text: priceColumnLabel } : column
        );

    const filteredColumns = relationKey === "outsource_materials"
        ? [{ text: "Mã vật tư", icon: <></> }, ...materialColumns]
        : materialColumns;

    const onRemoveMaterial = (material: Material) => {
        const removedMaterials = materials.filter(
            (item: any) => item.id !== material.id,
        );
        mergeProvider({ [relationKey]: removedMaterials });
    };

    const onChangeMaterialPrice = (material: Material, price: number) => {
        const updatedMaterials = materials.map((item: any) => (
            item.id === material.id ? { ...item, providerPrice: price } : item
        ));
        mergeProvider({ [relationKey]: updatedMaterials });
    };

    return (
        <>
            {isMaterialModalOpen && (
                <AddMaterialModal
                    selectedMaterials={materials}
                    open={isMaterialModalOpen}
                    handleClose={() => setIsMaterialModalOpen(false)}
                    mergeContactPerson={mergeProvider}
                    relationKey={relationKey}
                />
            )}
            <ContentWrapper>
                <PageContentWrapper>
                    <MaterialTableTitle>
                        {title}
                        <MaterialCounter>
                            {" "}
                            ({tableItems.length} {counterLabel}){" "}
                        </MaterialCounter>
                    </MaterialTableTitle>

                    {canEdit && (
                        <SearchBarWithSuggestion
                            menuList={allMaterials}
                            selectedItems={materials}
                            fetchItems={(variables: any) =>
                                fetchAllMaterials({ ...variables, page_size: Math.max(totalMaterials, 50), option: relationKey })
                            }
                            handleOnclickItem={handleSearchSelectMaterial}
                            placeholder={searchPlaceholder}
                        />
                    )}

                    <CustomTable
                        itemList={tableItems}
                        RowElement={MaterialRow}
                        TableColumns={filteredColumns}
                        extraRowProps={{
                            removeItem: onRemoveMaterial,
                            onChangeMaterialPrice: onChangeMaterialPrice,
                            canEdit,
                            isStorageKeeper,
                            relationKey,
                        }}
                    />
                    {tableItems.length === 0 && emptyText}

                    {canEdit && (
                        <MaterialButtonRow>
                            <CustomButton
                                title={addButtonLabel}
                                onClick={() => setIsMaterialModalOpen(true)}
                                variant={"contained"}
                            />
                        </MaterialButtonRow>
                    )}
                </PageContentWrapper>
            </ContentWrapper>
        </>
    );
};

export default MaterialList;
